/**
 * Settings Model Picker — Searchable model list.
 * Direct providers (OpenAI, Anthropic, Google, xAI): fetches from native API using user's key.
 * OpenRouter: fetches from public catalog (cached server-side).
 * Renders model list content ONLY (no modal chrome).
 * Parent (settings-page) provides the modal overlay/header/footer.
 * Emits: 'model-selected' { modelId, modelName }
 */

import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { settingsStyles } from '../../styles/settings-styles.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { llmService } from '../../services/llm-service.js';

export class SettingsModelPicker extends LitElement {
  static properties = {
    provider: { type: String },
    apiKey: { type: String },
    providerKey: { type: String },
    search: { type: String },
    _models: { state: true },
    _loading: { state: true },
    _error: { state: true },
  };

  static styles = [designSystem, componentStyles, settingsStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-height: 0;
    }

    /* ─── Model List ─── */
    .model-list {
      flex: 1;
      overflow-y: auto;
      padding: var(--space-2) 0;
      min-height: 0;
    }

    .model-row {
      display: flex;
      align-items: center;
      padding: var(--space-3) var(--space-6);
      cursor: pointer;
      gap: var(--space-4);
      transition: all var(--transition-fast);
      border-left: 3px solid transparent;
    }
    .model-row:hover {
      background: var(--color-bg-hover);
      border-left-color: var(--color-primary);
    }
    .model-row:active {
      background: var(--color-primary-light);
    }

    .provider-initial {
      width: 36px;
      height: 36px;
      border-radius: var(--radius-md);
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: var(--font-display);
      font-size: var(--text-xs);
      font-weight: var(--font-bold);
      letter-spacing: 0.02em;
      flex-shrink: 0;
      text-transform: uppercase;
      background: var(--color-primary-light);
      color: var(--color-primary);
      border: 1px solid var(--color-primary-subtle);
    }

    .model-info {
      flex: 1;
      min-width: 0;
    }
    .model-name {
      font-family: var(--font-body);
      font-size: var(--text-sm);
      font-weight: var(--font-medium);
      color: var(--color-text-primary);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.3;
    }
    .model-id {
      font-family: var(--font-mono);
      font-size: 0.6875rem;
      color: var(--color-text-tertiary);
      margin-top: 2px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.2;
    }

    .model-meta {
      display: flex;
      gap: var(--space-3);
      align-items: center;
      flex-shrink: 0;
    }
    .badge-ctx {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      font-weight: var(--font-bold);
      padding: 3px 10px;
      border-radius: var(--radius-full);
      background: var(--color-primary-subtle);
      color: var(--color-primary);
      white-space: nowrap;
    }
    .model-price {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      color: var(--color-text-secondary);
      white-space: nowrap;
      text-align: right;
      min-width: 110px;
      line-height: 1.5;
    }
    .price-row {
      display: flex;
      justify-content: space-between;
      gap: var(--space-2);
    }
    .price-label {
      color: var(--color-text-tertiary);
      font-weight: var(--font-normal);
    }
    .price-value {
      font-weight: var(--font-medium);
    }
    .price-free {
      color: var(--color-success-text);
      font-weight: var(--font-semibold);
    }

    /* ─── Loading / Error / Empty ─── */
    .loading, .error-msg, .no-results {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--space-16) var(--space-6);
      color: var(--color-text-tertiary);
      gap: var(--space-4);
      font-family: var(--font-body);
      font-size: var(--text-sm);
    }
    .state-icon {
      width: 48px;
      height: 48px;
      border-radius: var(--radius-full);
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .state-icon.loading-icon {
      background: var(--color-primary-light);
    }
    .state-icon.error-icon {
      background: var(--color-error-light);
      color: var(--color-error);
      font-size: 1.25rem;
    }
    .loading-spinner {
      width: 24px;
      height: 24px;
      border: 3px solid var(--color-primary-subtle);
      border-top-color: var(--color-primary);
      border-radius: 50%;
      animation: spin 0.8s linear infinite;
    }
    .error-msg { color: var(--color-error); }
    .no-results i {
      font-size: 1.5rem;
      opacity: 0.4;
    }
  `];

  constructor() {
    super();
    this.provider = 'all';
    this.apiKey = '';
    this.providerKey = '';
    this.search = '';
    this._models = [];
    this._loading = true;
    this._error = null;
    this._lastProvider = null;
    this._lastApiKey = null;
  }

  updated(changed) {
    if (changed.has('provider') && this.provider !== this._lastProvider) {
      this._lastProvider = this.provider;
      this._lastApiKey = this.apiKey;
      this._fetchModels();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this._lastProvider = this.provider;
    this._lastApiKey = this.apiKey;
    this._fetchModels();
  }

  async _fetchModels() {
    this._loading = true;
    this._error = null;
    try {
      const prov = this.provider || 'all';
      const isOpenRouter = prov === 'all' || prov === 'openrouter';

      if (!isOpenRouter && !this.apiKey && !this.providerKey) {
        throw new Error('Enter your API key first, then click Browse to see available models.');
      }

      this._models = await llmService.fetchModelsForProvider(
        isOpenRouter ? 'all' : prov,
        isOpenRouter ? '' : this.apiKey,
        isOpenRouter,
        this.providerKey || ''
      );
    } catch (e) {
      this._error = e.message || 'Failed to load models';
    } finally {
      this._loading = false;
    }
  }

  get _filtered() {
    if (!this._models || !this.search?.trim()) return this._models || [];
    const q = this.search.toLowerCase().trim();
    return this._models.filter(m =>
      m.id.toLowerCase().includes(q) ||
      m.name.toLowerCase().includes(q) ||
      (m.provider || '').toLowerCase().includes(q)
    );
  }

  get totalCount() { return this._models?.length || 0; }
  get filteredCount() { return this._filtered.length; }

  _formatCtx(len) {
    if (!len) return '?';
    if (len >= 1_000_000) return (len / 1_000_000).toFixed(1) + 'M';
    if (len >= 1_000) return Math.round(len / 1_000) + 'K';
    return String(len);
  }

  _formatPrice(perToken) {
    const perM = perToken * 1_000_000;
    if (perM === 0) return null;
    if (perM < 0.01) return '<$0.01';
    return '$' + perM.toFixed(2);
  }

  _isFree(m) {
    return (m.pricingPrompt * 1_000_000) === 0 && (m.pricingCompletion * 1_000_000) === 0;
  }

  _getInitial(provider) {
    if (!provider) return '?';
    const words = provider.trim().split(/\s+/);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return provider.substring(0, 2).toUpperCase();
  }

  _select(model) {
    this.dispatchEvent(new CustomEvent('model-selected', {
      detail: { modelId: model.id, modelName: model.name },
      bubbles: true, composed: true
    }));
  }

  render() {
    const filtered = this._filtered;
    const isAll = !this.provider || this.provider === 'all' || this.provider === 'openrouter';

    return html`
      ${iconStyles}
      <div class="model-list">
        ${this._loading ? html`
          <div class="loading">
            <div class="state-icon loading-icon">
              <div class="loading-spinner"></div>
            </div>
            Loading models...
          </div>
        ` : this._error ? html`
          <div class="error-msg">
            <div class="state-icon error-icon">
              <i class="fa-sharp fa-light fa-circle-exclamation"></i>
            </div>
            <div>${this._error}</div>
            <button class="btn btn-primary btn-sm" @click=${() => this._fetchModels()}>
              <i class="fa-sharp fa-light fa-arrows-rotate"></i>
              Retry
            </button>
          </div>
        ` : filtered.length === 0 ? html`
          <div class="no-results">
            <i class="fa-sharp fa-light fa-magnifying-glass"></i>
            No models match "${this.search}"
          </div>
        ` : filtered.map(m => {
            const hasCtx = m.contextLength > 0;
            const hasPrice = m.pricingPrompt > 0 || m.pricingCompletion > 0;
            const hasMeta = hasCtx || hasPrice;
            return html`
            <div class="model-row" @click=${() => this._select(m)}>
              ${isAll ? html`
                <div class="provider-initial">
                  ${this._getInitial(m.provider)}
                </div>
              ` : ''}
              <div class="model-info">
                <div class="model-name">${m.name}</div>
                <div class="model-id">${m.id}</div>
              </div>
              ${hasMeta ? html`
                <div class="model-meta">
                  ${hasCtx ? html`<span class="badge-ctx">${this._formatCtx(m.contextLength)}</span>` : ''}
                  ${hasPrice ? html`
                    <div class="model-price">
                      ${this._isFree(m) ? html`
                        <span class="price-free">Free</span>
                      ` : html`
                        <div class="price-row">
                          <span class="price-label">In</span>
                          <span class="price-value">${this._formatPrice(m.pricingPrompt)}</span>
                        </div>
                        <div class="price-row">
                          <span class="price-label">Out</span>
                          <span class="price-value">${this._formatPrice(m.pricingCompletion)}</span>
                        </div>
                      `}
                    </div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
          `})}
      </div>
    `;
  }
}

customElements.define('settings-model-picker', SettingsModelPicker);
